import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@components/Icon';
import SEO from '@components/seo/SEO';
import { BackTop, Card, Steps, Divider, Drawer } from 'antd';

// 创建图标封装组件，与LandingPage中相同
const SafeIcon = ({ icon: IconName, useFa = true, ...props }) => {
  if (typeof IconName === 'string' && Icon[IconName]) {
    const IconComponent = Icon[IconName];
    return <IconComponent useFa={useFa} {...props} />;
  }
  
  if (typeof IconName === 'function' || typeof IconName === 'object') {
    return <IconName useFa={useFa} {...props} />;
  }
  
  return null;
};

const QuickStart = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <SEO 
        title="快速上手 - 图美啦使用指南 | 在线图片美化工具"
        description="图美啦快速上手指南，帮助您立即开始使用我们的在线图片美化工具。轻松学习图片上传、编辑、标注和分享功能。"
        keywords="图美啦使用指南,图片美化教程,图片编辑入门,在线工具教学,图片标注指南,快速上手"
        canonicalUrl="https://snapbeautify.toolooz.com/quick-start"
      />
      {/* 导航栏 */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center text-2xl font-bold text-purple-600 dark:text-purple-400">
                <img src="/favicon.svg" alt="图美啦" width="32" height="32" className="mr-2" />
                图美啦
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="Home" size={16} className="mr-1" />
                首页
              </Link>
              <Link to="/quick-start" className="font-medium text-purple-600 dark:text-purple-400 flex items-center">
                <SafeIcon icon="BookOpen" size={16} className="mr-1" />
                快速上手
              </Link>
              <Link to="/about" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="User" size={16} className="mr-1" />
                关于我们
              </Link>
            </nav>
            <div className="flex md:hidden">
              <button 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setMobileMenuOpen(true)}
              >
                <SafeIcon icon="Menu" size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* 移动设备导航菜单 */}
        <Drawer
          title="导航菜单"
          placement="right"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={250}
        >
          <div className="flex flex-col space-y-4">
            <Link 
              to="/" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Home" size={16} className="mr-1" />
              首页
            </Link>
            <Link 
              to="/quick-start" 
              className="font-medium text-purple-600 dark:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="BookOpen" size={16} className="mr-1" />
              快速上手
            </Link>
            <Link 
              to="/about" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="User" size={16} className="mr-1" />
              关于我们
            </Link>
            <Link 
              to="/features" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Star" size={16} className="mr-1" />
              功能介绍
            </Link>
            <Link 
              to="/faq" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="HelpCircle" size={16} className="mr-1" />
              常见问题
            </Link>
            <div className="border-t border-gray-200 dark:border-gray-700 my-2 pt-2">
              <Link 
                to="/privacy" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Shield" size={16} className="mr-1" />
                隐私政策
              </Link>
              <Link 
                to="/terms" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="FileText" size={16} className="mr-1" />
                使用条款
              </Link>
              <Link 
                to="/copyright" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Award" size={16} className="mr-1" />
                版权声明
              </Link>
            </div>
          </div>
        </Drawer>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <div className="p-8">
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <SafeIcon icon="Rocket" className="mr-3 text-purple-600 dark:text-purple-400" size={28} useFa={true} />
              快速上手
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-4">更新日期: 2025年3月22日</span>
            </h1>

            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-600 dark:text-gray-300 text-lg">
                欢迎来到图美啦！我们提供了一个简单好用的工具，帮你一键美化各种截图和图片。不管你是做PPT演示、写教程文档，还是分享产品界面，都能轻松制作出好看的效果图。下面来看看怎么用吧！
              </p>
              
              <div className="my-8">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-white flex items-center">
                  <SafeIcon icon="Info" className="mr-2 text-purple-500 dark:text-purple-400" size={24} useFa={true} />
                  这是什么工具？
                </h2>
                <div className="bg-purple-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
                  <p className="text-gray-700 dark:text-gray-300">
                    图美啦是一个专门用来美化截图的在线工具，不用下载安装任何软件，打开网页就能用。它特别适合以下情况：
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>产品经理做演示PPT，需要美化软件界面截图</li>
                    <li>教程作者需要给截图添加箭头和文字说明</li>
                    <li>社交媒体分享时，想让截图看起来更精美</li>
                    <li>电商卖家展示产品图片，需要加上说明文字</li>
                    <li>开发者分享界面设计，想让截图更专业</li>
                  </ul>
                </div>
              </div>
              
              <div className="my-8">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-white flex items-center">
                  <SafeIcon icon="Play" className="mr-2 text-purple-500 dark:text-purple-400" size={24} useFa={true} />
                  怎么使用
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* 步骤1 */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-600 dark:text-blue-300">
                        <div className="text-xl font-bold">1</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">上传图片</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Upload" className="text-blue-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      点击首页的"本地上传图片"按钮，或者直接复制图片后粘贴(Ctrl+V)到网站。
                    </p>
                  </div>
                  
                  {/* 步骤2 */}
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center text-green-600 dark:text-green-300">
                        <div className="text-xl font-bold">2</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">添加背景框</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Square" className="text-green-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      在右侧面板选择喜欢的背景样式，调整圆角、阴影和边距等参数。
                    </p>
                  </div>
                  
                  {/* 步骤3 */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center text-amber-600 dark:text-amber-300">
                        <div className="text-xl font-bold">3</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">添加标注</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Edit2" className="text-amber-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      用左侧工具栏中的箭头、形状、文字等工具，在图片上添加说明和标记。
                    </p>
                  </div>
                  
                  {/* 步骤4 */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-purple-300">
                        <div className="text-xl font-bold">4</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">调整效果</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Sliders" className="text-purple-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      通过滤镜和调整工具，优化图片效果，让整体视觉更加协调。
                    </p>
                  </div>
                  
                  {/* 步骤5 */}
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-5 transform transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center text-red-600 dark:text-red-300">
                        <div className="text-xl font-bold">5</div>
                      </div>
                    </div>
                    <h3 className="text-center text-lg font-semibold text-gray-800 dark:text-white mb-2">保存分享</h3>
                    <div className="flex justify-center mb-3">
                      <SafeIcon icon="Download" className="text-red-500" size={24} useFa={true} />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-center text-sm">
                      点击右下角"导出"按钮，选择格式下载，或直接复制到剪贴板分享。
                    </p>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-gray-600 dark:text-gray-300 text-center">
                    <SafeIcon icon="Info" className="inline-block mr-2 text-blue-500" size={18} useFa={true} />
                    整个流程非常简单，几分钟就能完成一张精美图片的制作
                  </p>
                </div>
              </div>
              
              <div className="my-8">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-white flex items-center">
                  <SafeIcon icon="Zap" className="mr-2 text-purple-500 dark:text-purple-400" size={24} useFa={true} />
                  使用技巧
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="bg-blue-50 dark:bg-gray-700 border-0 shadow hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center mb-3">
                      <SafeIcon icon="Keyboard" size={18} className="mr-2 text-blue-500" useFa={true} />
                      快捷键
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      使用快捷键提高效率！常用的有：
                      <code className="mx-1 px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-sm">Ctrl+V</code>粘贴图片、
                      <code className="mx-1 px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-sm">Del</code>删除选中元素、
                      <code className="mx-1 px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-sm">空格+拖动</code>移动画布。
                    </p>
                  </Card>
                  
                  <Card className="bg-green-50 dark:bg-gray-700 border-0 shadow hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center mb-3">
                      <SafeIcon icon="Crop" size={18} className="mr-2 text-green-500" useFa={true} />
                      裁剪与调整
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      使用裁剪工具专注于图片的重要部分。拖动控制点调整图片大小，双击图片开启精确调整模式。
                    </p>
                  </Card>
                  
                  <Card className="bg-amber-50 dark:bg-gray-700 border-0 shadow hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center mb-3">
                      <SafeIcon icon="Layers" size={18} className="mr-2 text-amber-500" useFa={true} />
                      图层管理
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      添加的每个元素都是独立图层。选中后可以调整位置、大小和样式。按住Shift选择多个元素进行批量操作。
                    </p>
                  </Card>
                  
                  <Card className="bg-red-50 dark:bg-gray-700 border-0 shadow hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center mb-3">
                      <SafeIcon icon="Share2" size={18} className="mr-2 text-red-500" useFa={true} />
                      分享技巧
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      导出图片后，可以直接拖放到微信、QQ等聊天窗口分享。使用"复制"功能可快速分享到各类应用中。
                    </p>
                  </Card>
                </div>
              </div>
              
              {/* 视频截图功能说明 - 新增 */}
              <div className="my-8">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-white flex items-center">
                  <SafeIcon icon="Video" className="mr-2 text-green-500 dark:text-green-400" size={24} useFa={true} />
                  视频截图功能
                </h2>
                
                <div className="bg-green-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
                  <p className="text-gray-700 dark:text-gray-300">
                    视频截图功能让您可以从视频中捕获精彩瞬间，然后使用我们的编辑工具进行美化。这个功能特别适合：
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>教程制作者需要从视频教程中提取关键画面</li>
                    <li>游戏玩家想要分享游戏中的精彩瞬间</li>
                    <li>视频分析师需要标注和比较视频中的不同场景</li>
                    <li>产品演示视频需要提取重点界面进行说明</li>
                  </ul>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-green-50 dark:bg-gray-700 border-0 shadow hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center mb-3">
                      <SafeIcon icon="Film" size={18} className="mr-2 text-green-500" useFa={true} />
                      获取视频截图
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      进入视频截图页面，上传本地视频文件（支持常见视频格式，最大10GB），播放视频并在喜欢的画面点击"截图"按钮即可捕获当前帧。
                      <Link to="/video-capture" className="block mt-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300">
                        前往视频截图 →
                      </Link>
                    </p>
                  </Card>
                  
                  <Card className="bg-green-50 dark:bg-gray-700 border-0 shadow hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center mb-3">
                      <SafeIcon icon="Edit" size={18} className="mr-2 text-green-500" useFa={true} />
                      编辑截图
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      对视频截图进行编辑非常简单，点击截图列表中的"编辑"按钮，将自动跳转到编辑器，您可以在那里添加背景、标注、文字等元素，编辑完成后可以返回视频截图页面。已编辑过的截图会显示"已编辑"标记。
                    </p>
                  </Card>
                </div>
                
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-gray-600 dark:text-gray-300 text-center">
                    <SafeIcon icon="Info" className="inline-block mr-2 text-blue-500" size={18} useFa={true} />
                    视频截图功能支持在编辑后返回时保留原视频和其他截图，方便您连续处理多个截图。
                  </p>
                </div>
              </div>
              
              <Divider className="my-10" />
              
              <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-6">
                <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white flex items-center">
                  <SafeIcon icon="HelpCircle" className="mr-2 text-purple-500 dark:text-purple-400" size={24} useFa={true} />
                  需要更多帮助？
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  如果您在使用过程中遇到任何问题，或有功能建议，欢迎随时联系我们！我们会尽快回复并解决您的问题。
                </p>
                <div className="flex items-center">
                  <SafeIcon icon="Mail" size={20} className="mr-2 text-purple-500 dark:text-purple-400" />
                  <a href="mailto:support@toolooz.com" className="text-purple-600 dark:text-purple-400 hover:underline">
                    support@toolooz.com
                  </a>
                </div>
                <p className="mt-4 text-gray-600 dark:text-gray-400 text-sm">
                  我们通常会在1-2个工作日内回复您的邮件。
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 页脚区域 */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <Link to="/" className="flex items-center text-xl font-bold text-white">
                <img src="/favicon.svg" alt="图美啦" width="24" height="24" className="mr-2" />
                图美啦
              </Link>
            </div>
            
            <div className="flex space-x-8">
              <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors text-sm">隐私政策</Link>
              <Link to="/terms" className="text-gray-300 hover:text-white transition-colors text-sm">使用条款</Link>
              <Link to="/copyright" className="text-gray-300 hover:text-white transition-colors text-sm">版权声明</Link>
            </div>
            
            <div className="mt-4 md:mt-0 text-gray-400 text-sm">
              © {new Date().getFullYear()} 图美啦 | SnapBeautify
            </div>
          </div>
        </div>
      </footer>
      
      <BackTop>
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg">
          <SafeIcon icon="ChevronUp" size={20} />
        </div>
      </BackTop>
    </div>
  );
};

export default QuickStart; 